import React from "react";
import { useLang, useSettings, assetUrl, cldUrl } from "../lib/providers";
import { Button } from "./ui/button";
import { ArrowRight, Shield, Truck, BadgeCheck, Zap, Heart, Star, Flame } from "lucide-react";

const HERO_ICONS = { shield: Shield, truck: Truck, badge: BadgeCheck, zap: Zap, heart: Heart, star: Star, flame: Flame };

export default function Hero() {
  const { lang } = useLang();
  const { settings } = useSettings();
  if (!settings.hero_enabled) return null;

  const get = (fr, ar) => (lang === "ar" ? settings[ar] : settings[fr]);
  const line1 = get("hero_title_line1_fr", "hero_title_line1_ar");
  const line2 = get("hero_title_line2_fr", "hero_title_line2_ar");
  const line3 = get("hero_title_line3_fr", "hero_title_line3_ar");
  const kicker = get("hero_kicker_fr", "hero_kicker_ar");
  const desc = get("hero_desc_fr", "hero_desc_ar");
  const cta_shop = get("hero_cta_shop_fr", "hero_cta_shop_ar");
  const cta_wa = get("hero_cta_wa_fr", "hero_cta_wa_ar");
  const phoneDz = (settings.whatsapp_dz || "").replace(/\D/g, "");

  const bgVideo = settings.hero_bg_video_url ? assetUrl(settings.hero_bg_video_url) : "";
  const bgImg = settings.hero_bg_image_url ? cldUrl(settings.hero_bg_image_url, { w: 1920 }) : "";

  const stats = (settings.hero_stats_items || []).filter((s) => s.visible !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <section id="top" className="relative theme-section overflow-hidden grain court-lines-overlay" data-testid="hero-section">
      {bgVideo ? (
        <video src={bgVideo} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-30" />
      ) : bgImg ? (
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${bgImg})` }} />
      ) : null}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            {kicker && (
              <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-neon mb-6 border border-neon/30 px-3 py-1.5">
                <span className="w-1.5 h-1.5 bg-neon rounded-full animate-pulse" />
                <span dangerouslySetInnerHTML={{
                  __html: (kicker || "").replace(/(⏳|⌛|🕐|🕑|🕒|🕓|🕔|🕕|🕖|🕗|🕘|🕙|🕚|🕛)/g,
                    '<span class="inline-block hero-hourglass">$1</span>')
                }} />
              </p>
            )}
            <h1 className="font-heading font-black uppercase leading-[0.9] tracking-tighter text-4xl sm:text-6xl lg:text-7xl">
              {line1}<br />{line2} <span className="text-neon">{line3}</span>
            </h1>
            {desc && <p className="mt-8 text-base sm:text-lg text-white/70 max-w-xl">{desc}</p>}
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest px-6 py-6 text-sm h-auto" data-testid="hero-cta-shop">
                <a href="#products">{cta_shop} <ArrowRight className="w-4 h-4 ms-2" /></a>
              </Button>
              <Button variant="outline" asChild className="border-white/20 text-white hover:bg-white/5 rounded-none font-black uppercase tracking-widest px-6 py-6 text-sm h-auto bg-transparent" data-testid="hero-cta-wa">
                <a href="#b2b">{cta_wa}</a>
              </Button>
            </div>

            {settings.hero_stats_enabled && stats.length > 0 && (
              <div className={`mt-12 grid gap-4 max-w-lg ${stats.length === 1 ? "grid-cols-1" : stats.length === 2 ? "grid-cols-2" : "grid-cols-3"}`} data-testid="hero-stats">
                {stats.map((s) => {
                  const Icon = HERO_ICONS[s.icon] || BadgeCheck;
                  return (
                    <div key={s.id} className="border-s border-white/10 ps-4">
                      {s.image_url ? (
                        <img
                          src={cldUrl(s.image_url, { w: 160 })}
                          alt=""
                          style={{ width: `${settings.hero_stats_logo_size_px || 24}px`, height: `${settings.hero_stats_logo_size_px || 24}px` }}
                          className="object-contain mb-1"
                        />
                      ) : (
                        <Icon
                          style={{ width: `${settings.hero_stats_logo_size_px || 24}px`, height: `${settings.hero_stats_logo_size_px || 24}px` }}
                          className="text-neon mb-1"
                        />
                      )}
                      <div className="font-heading font-black text-xl sm:text-2xl">{s.value}</div>
                      <div className="text-[10px] tracking-widest uppercase text-white/50 leading-tight mt-1">
                        {lang === "ar" ? s.label_ar : s.label_fr}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 fade-up relative" style={{ animationDelay: "0.2s" }}>
            <div
              className="relative aspect-square max-w-md mx-auto"
              style={{
                transform: `scale(${(settings.logo_size_px || 48) / 48})`,
                transformOrigin: "center",
              }}
            >
              <div className="absolute inset-0 bg-neon/10 blur-3xl rounded-full" />
              <div className="absolute inset-4 border border-neon/20 rotate-45" />
              <div className="absolute inset-8 border border-clay/30 rotate-12" />
              <img
                src={cldUrl(settings.logo_url, { w: 800 })}
                srcSet={`${cldUrl(settings.logo_url, { w: 400 })} 400w, ${cldUrl(settings.logo_url, { w: 800 })} 800w, ${cldUrl(settings.logo_url, { w: 1200 })} 1200w`}
                sizes="(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 480px"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                alt={settings.site_name}
                style={{
                  opacity: (settings.logo_opacity ?? 100) / 100,
                  borderRadius: settings.logo_shape === "circle" ? "50%" : settings.logo_shape === "rounded" ? "24px" : "0",
                  objectFit: "cover",
                }}
                className="relative z-10 w-full h-full drop-shadow-[0_0_40px_rgba(223,255,0,0.25)]"
                data-testid="hero-logo"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
